import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'Services', href: '/services' },
    { name: 'FAQ', href: '/faq' }
  ];

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 nav-glass transition-all duration-300 ${
        isScrolled ? 'scrolled' : ''
      }`}
      style={{ 
        backgroundColor: isScrolled 
          ? 'rgba(26, 28, 27, 0.95)' 
          : 'rgba(26, 28, 27, 0.8)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderBottom: `1px solid ${isScrolled ? 'rgba(63, 72, 22, 0.4)' : 'rgba(63, 72, 22, 0.2)'}`,
        boxShadow: isScrolled ? '0 8px 32px rgba(0, 0, 0, 0.3)' : 'none'
      }}
    >
      <div className="container max-w-6xl mx-auto px-6">
        <div className="flex justify-between items-center py-4">
          {/* Logo with animation */}
          <Link 
            to="/" 
            className="text-xl font-bold transition-all duration-300 hover:scale-105 text-glow"
            style={{ color: 'var(--text-primary)' }}
          >
            AddVantage
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex space-x-2">
            {navigation.map((item, index) => (
              <Link
                key={item.name}
                to={item.href}
                className="px-4 py-2 text-base font-medium rounded-full transition-all duration-300 hover:scale-105 relative overflow-hidden"
                style={{ 
                  color: location.pathname === item.href 
                    ? 'var(--brand-primary)' 
                    : 'var(--text-primary)',
                  backgroundColor: location.pathname === item.href
                    ? 'rgba(217, 251, 6, 0.1)'
                    : 'transparent',
                  animationDelay: `${index * 0.1}s`
                }}
                onMouseEnter={(e) => {
                  if (location.pathname !== item.href) {
                    e.target.style.backgroundColor = 'rgba(217, 251, 6, 0.05)';
                    e.target.style.transform = 'scale(1.05)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (location.pathname !== item.href) {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.transform = 'scale(1)';
                  }
                }}
              >
                {item.name}
              </Link>
            ))}
          </div>

          {/* Mobile Menu Button with animation */}
          <button
            className="md:hidden p-2 rounded-lg transition-all duration-300 hover:scale-110 icon-hover"
            style={{ 
              color: 'var(--text-primary)',
              backgroundColor: isMenuOpen ? 'rgba(217, 251, 6, 0.1)' : 'transparent'
            }}
            onClick={toggleMenu}
            aria-label="Toggle menu"
          >
            <div className="relative w-6 h-6">
              <Menu 
                className={`h-6 w-6 absolute transition-all duration-300 ${
                  isMenuOpen ? 'rotate-90 opacity-0' : 'rotate-0 opacity-100'
                }`} 
              />
              <X 
                className={`h-6 w-6 absolute transition-all duration-300 ${
                  isMenuOpen ? 'rotate-0 opacity-100' : '-rotate-90 opacity-0'
                }`} 
              />
            </div>
          </button>
        </div>

        {/* Mobile Navigation with slide animation */}
        <div 
          className={`md:hidden overflow-hidden transition-all duration-300 ${
            isMenuOpen ? 'max-h-64 opacity-100' : 'max-h-0 opacity-0'
          }`}
          style={{ borderTop: isMenuOpen ? '1px solid rgba(63, 72, 22, 0.2)' : 'none' }}
        >
          <div className="py-4 space-y-2 stagger-container">
            {navigation.map((item, index) => (
              <Link
                key={item.name}
                to={item.href}
                className="block py-3 px-4 text-base font-medium transition-all duration-300 rounded-lg hover:scale-105"
                style={{ 
                  color: location.pathname === item.href
                    ? 'var(--brand-primary)'
                    : 'var(--text-primary)',
                  backgroundColor: location.pathname === item.href
                    ? 'rgba(217, 251, 6, 0.1)'
                    : 'transparent',
                  transform: isMenuOpen ? 'translateX(0)' : 'translateX(-20px)',
                  transitionDelay: `${index * 0.1}s`
                }}
                onClick={() => setIsMenuOpen(false)}
                onMouseEnter={(e) => {
                  if (location.pathname !== item.href) {
                    e.target.style.backgroundColor = 'rgba(217, 251, 6, 0.05)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (location.pathname !== item.href) {
                    e.target.style.backgroundColor = 'transparent';
                  }
                }}
              >
                {item.name}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;